import React, { useState } from "react";
import ShopFooter from "./ShopFooter";
import { VIRTUAL_CURRENCY_SHOP_NOTE } from "../../utils/virtualCurrencyDisclaimer";


interface BuyCoinsWithRubiesProps {
  pay_amount: number;
  /** Full ruby price when a sale is active; shown struck through. */
  original_pay_amount?: number;
  receive_amount: number;
  closeModal: (success?: boolean) => void;
  setIsAnimatingCoins: (isAnimating: boolean) => void;
  onPlayCoinsSound?: () => void;
  updateUserAmount: (
    offerId: string,
    offerType: string,
    meta?: { pay_amount: number; receive_amount: number; offer_name?: string }
  ) => void;
  coinId: number;
  offerType: string;
  offerName?: string;
}

export default function BuyCoinsWithRubies({
  pay_amount,
  original_pay_amount,
  receive_amount,
  closeModal,
  setIsAnimatingCoins,
  onPlayCoinsSound,
  updateUserAmount,
  coinId,
  offerType,
  offerName,
}: BuyCoinsWithRubiesProps) {
  const [isSuccess, setIsSuccess] = useState(false);

  const formatCoins = (amount: number) => {
    return new Intl.NumberFormat('en-IN').format(amount);
  };

  function handlePaymentSuccess() {
    void updateUserAmount(String(coinId), offerType, {
      pay_amount,
      receive_amount,
      offer_name: offerName,
    });
    setIsSuccess(true);
  }

  function handleDone() {
    // Play sound in same synchronous stack as user click (same pattern as Squares) so browser allows it
    onPlayCoinsSound?.();
    // Close modal first
    closeModal(true);
    // Trigger animation after modal closes so it's visible in Wallet
    setTimeout(() => {
      setIsAnimatingCoins(true);
      // Stop animation after 3 seconds
      setTimeout(() => {
        setIsAnimatingCoins(false);
      }, 3000);
    }, 100);
  }

  // Success view
  if (isSuccess) {
    return (
      <div className="w-[100%]">
        {/* Success Header */}
        <div 
          className="w-[100%] py-[20px] flex flex-row justify-center items-center"
          style={{ backgroundColor: '#401A6E' }}
        >
          <p className="font-anton text-white text-[24px]">SUCCESS!</p>
        </div>

        {/* White Content Panel */}
        <div 
          className="w-[100%] bg-white px-[20px] py-[10px] flex flex-col items-center justify-center"
          style={{ backgroundColor: '#FFFFFF' }}
        >
          {/* Main Message */}
          <p className="font-anton text-main text-[20px]">
            COINS ADDED TO WALLET!
          </p>

          {/* Coin Icon */}
          <img 
            className="w-[90px] h-[90px]" 
            src="/assets/currency-coin.png" 
            alt="coin"
          />

          {/* Amount Added */}
          <p className="font-anton text-main text-[32px]">
            +{formatCoins(receive_amount)}
          </p>
          {original_pay_amount != null && original_pay_amount > pay_amount ? (
            <p className="mt-[2px] flex items-center gap-[4px] bg-red px-[10px] py-[2px] font-anton text-[16px] uppercase text-white">
              You saved {original_pay_amount - pay_amount}
              <img className="h-[18px] w-[18px]" src="/assets/currency-ruby.png" alt="rubies" />!
            </p>
          ) : null}
        </div>

        <div className="w-[100%] flex flex-row justify-center items-center py-[20px]">
          {/* DONE Button */}
          <button
            onClick={handleDone}
            style={{
              width: "290px",
              height: "50px",
              backgroundColor: "#D2FF00",
              color: "#1B062F",
              fontWeight: "400",
              fontFamily: "Anton",
              fontSize: "16px",
              lineHeight: "24px",
              textAlign: "center",
            }}
          >
            DONE
          </button>
        </div>

        <ShopFooter />
      </div>
    );
  }

  // Purchase view (original)
  return (
    <div className="w-[100%]">
      <div className="shop-content p-4 ml-[7px]">
        <p className="font-anton">SHOP</p>
      </div>
      <div className="flex flex-row items-center justify-between h-[81px] bg-white px-[15px]">
        <div className="flex flex-row items-center gap-[10px]">
          <img className="w-[50px] h-[50px]" src="/assets/currency-coin.png" />
          <p className="font-[400] text-main font-[20px] font-anton leading-[15px]">
            GET <span className="lowercase">x</span>{receive_amount} COINS
          </p>
        </div>
        <div className="flex flex-row items-center gap-[8px]">
          {original_pay_amount != null && original_pay_amount > pay_amount ? (
            <p className="font-anton text-[14px] text-gray-400 line-through" aria-label={`Was ${original_pay_amount} rubies`}>
              {original_pay_amount}
            </p>
          ) : null}
          <div className="w-[90px] h-[40px] bg-lime flex flex-row items-center justify-left pl-[10px]">
            <img
              className="w-[30px] h-[30px] flex flex-row justify-around items-center mr-[5px]"
              src="/assets/currency-ruby.png"
            />
            <p className="text-main font-[400] font-anton text-[16px] leading-[24px]">
              {pay_amount}
            </p>
          </div>
        </div>
      </div>
      <p className="mt-3 px-4 text-center font-chakra text-[12px] leading-snug text-white/70 normal-case">
        {VIRTUAL_CURRENCY_SHOP_NOTE}
      </p>
      <div className="w-full flex flex-row justify-center items-center mt-[20px]">
        <button
          onClick={handlePaymentSuccess}
          style={{
            width: "290px",
            height: "50px",
            backgroundColor: "#D2FF00",
            color: "#1B062F",
            fontWeight: "400",
            fontFamily: "Anton",
            fontSize: "16px",
            lineHeight: "24px",
            textAlign: "center",
          }}
        >
          GET COINS
        </button>
      </div>
      
      <ShopFooter className="mt-[18px]" />
    </div>
  );
}
